import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HomeScreen } from './components/home/HomeScreen';
import { WorkoutCreator } from './components/generate/WorkoutCreator';
import { WorkoutOverview } from './components/overview/WorkoutOverview';
import { WorkoutSession } from './components/session/WorkoutSession';
import { LandingPage } from './components/landing/LandingPage';
import { BetaPage } from './components/beta/BetaPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Footer } from './components/ui/Footer';
import { useWorkoutStore } from './lib/stores/workoutStore';
import { useAuthStore } from './lib/stores/authStore';
import { useAnalytics } from './lib/posthog';
import { Workout } from './types/workout';

type AppView = 'home' | 'generate' | 'overview' | 'session';
type WorkoutSource = 'generate' | 'quick' | 'list';

function GymApp() {
  const [view, setView] = useState<AppView>('home');
  const [activeWorkout, setActiveWorkout] = useState<Workout | null>(null);
  const [overviewSource, setOverviewSource] = useState<WorkoutSource>('list');
  const { pageView } = useAnalytics();
  const { 
    setActiveWorkout: setStoreActiveWorkout, 
    presetWorkouts, 
    generatedWorkouts,
    loadPresets,
    loadGeneratedWorkouts 
  } = useWorkoutStore();
  
  // Track internal view changes as virtual page views
  useEffect(() => {
    const path = `/app/${view}`;
    const title = view === 'home' ? 'Home' 
      : view === 'generate' ? 'Generate Workout'
      : view === 'overview' ? 'Workout Overview'
      : view === 'session' ? 'Workout Session'
      : 'Gym App';
    pageView(path, title);
  }, [view, pageView]);
  useEffect(() => {
    // Load presets once on mount
    // Note: loadPresets is idempotent (just sets state from constant), 
    // but we only need to call it once per component mount
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - loadPresets is stable and only needs to run once

  const handleGenerateClick = () => {
    setView('generate');
  };

  const handleGenerateComplete = async (workout: Workout) => {
    setActiveWorkout(workout);
    setStoreActiveWorkout(workout);
    setOverviewSource('generate' as WorkoutSource);
    setView('overview');
  };

  const handleGenerateCancel = () => {
    setView('home');
  };

  const handleWorkoutSelect = async (workoutIdOrWorkout: string | Workout) => {
    // If a Workout object is passed directly, use it
    // This happens for quick workouts
    if (typeof workoutIdOrWorkout !== 'string') {
      setActiveWorkout(workoutIdOrWorkout);
      setStoreActiveWorkout(workoutIdOrWorkout);
      // Determine if it's a quick workout (check if it was just generated)
      setOverviewSource('quick');
      setView('overview');
      return;
    }

    // Otherwise, treat it as a workoutId and look it up
    const workoutId = workoutIdOrWorkout;
    // Try to find in preset workouts first
    let workout = presetWorkouts.find(w => w.id === workoutId);
    
    // If not found, try to load from API
    if (!workout) {
      if (generatedWorkouts.length === 0) {
        await loadGeneratedWorkouts();
      }
      workout = generatedWorkouts.find(w => w.id === workoutId);
    }
    
    if (workout) {
      setActiveWorkout(workout);
      setStoreActiveWorkout(workout);
      setOverviewSource('list' as WorkoutSource);
      setView('overview');
    }
  };

  const handleOverviewStart = (workout: Workout) => {
    setActiveWorkout(workout);
    setStoreActiveWorkout(workout);
    setView('session');
  };

  const handleOverviewSaveOnly = (workout: Workout) => {
    // Workout has been saved, go back to home
    setActiveWorkout(null);
    setStoreActiveWorkout(null);
    setOverviewSource('list' as WorkoutSource);
    // Refresh the workout list to show the saved workout
    loadGeneratedWorkouts();
    setView('home');
  };

  const handleOverviewBack = () => {
    // Navigate back based on where we came from
    if (overviewSource === 'generate') {
      setView('generate');
    } else {
      setView('home');
    }
    // Clear overview source when leaving overview
    setOverviewSource('list' as WorkoutSource);
  };

  const handleSessionExit = () => {
    setView('home');
    setActiveWorkout(null);
    setStoreActiveWorkout(null);
    // Refresh workout list to show the completed workout
    loadGeneratedWorkouts();
  };

  if (view === 'generate') {
    return (
      <WorkoutCreator
        onComplete={handleGenerateComplete}
        onCancel={handleGenerateCancel}
      />
    );
  }

  if (view === 'overview' && activeWorkout) {
    return (
      <WorkoutOverview
        workout={activeWorkout}
        onStart={handleOverviewStart}
        onBack={handleOverviewBack}
        onSaveOnly={handleOverviewSaveOnly}
        overviewSource={overviewSource}
      />
    );
  }

  if (view === 'session' && activeWorkout) {
    return (
      <WorkoutSession
        workout={activeWorkout}
        onExit={handleSessionExit}
      />
    );
  }

  return (
    <HomeScreen
      onGenerateClick={handleGenerateClick}
      onWorkoutSelect={handleWorkoutSelect}
    />
  );
}

function LandingPageWrapper() {
  const navigate = useNavigate();
  
  const handleEnterApp = () => {
    navigate('/app');
  };

  return <LandingPage onEnterApp={handleEnterApp} />;
}

// Separate component that can use useLocation inside Router context
function AppRoutes() {
  const location = useLocation();
  const { pageView } = useAnalytics();
  
  // Track page views on route changes
  useEffect(() => {
    const title = location.pathname === '/' ? 'Landing Page'
      : location.pathname === '/beta' ? 'Beta Application'
      : location.pathname === '/app' ? 'Gym App'
      : document.title;
    pageView(location.pathname, title);
  }, [location.pathname, pageView]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPageWrapper />} />
          <Route path="/beta" element={<BetaPage />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <GymApp />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

function App() {
  const { initialize: initializeAuth, isLoading: authLoading } = useAuthStore();

  useEffect(() => {
    // Initialize auth and get unsubscribe function
    const unsubscribe = initializeAuth();
    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [initializeAuth]);

  // Show loading state until auth check is complete
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-neutral-500">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;

